/**
 * ideaFeedback.ts — Idea Feedback REST API helpers
 * Base URL: REACT_APP_API_URL (default: http://localhost:5000/api)
 * Endpoint: GET /idea-feedback — fetch feedback for an idea
 */

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

/** Fetch feedback for a specific idea */
export async function fetchIdeaFeedback(token: string, ideaId: string) {
  const query = new URLSearchParams();
  query.set("ideaId", ideaId);
  const res = await fetch(`${BASE}/idea-feedback?${query.toString()}`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error("Failed to fetch idea feedback");
  return res.json(); // { items: [], stats: { averageRating, ratingCount } }
}

/** Validate if a string is a valid MongoDB ObjectId */
function isValidObjectId(id: string): boolean {
  return /^[0-9a-fA-F]{24}$/.test(id);
}

/** Submit feedback for an idea */
export async function submitIdeaFeedback(
  token: string,
  payload: {
    hackathonId: string;
    teamId: string;
    ideaId: string;
    comment: string;
    rating?: number | null;
  }
) {
  // Validate required fields are valid ObjectIds
  if (!isValidObjectId(payload.hackathonId)) {
    throw new Error("Invalid hackathonId format");
  }
  if (!isValidObjectId(payload.teamId)) {
    throw new Error("Invalid teamId format");
  }
  if (!isValidObjectId(payload.ideaId)) {
    throw new Error("Invalid ideaId format");
  }
  if (!payload.comment || typeof payload.comment !== "string" || payload.comment.trim().length === 0) {
    throw new Error("Comment is required");
  }

  const res = await fetch(`${BASE}/idea-feedback`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message ?? "Failed to submit feedback");
  return data;
}
